/**
 * useApiKeyCount
 *
 * Lightweight wrapper around `getApiKeys` that returns a key count. By default
 * counts active keys only; pass `{ fetchAll: true }` to include revoked keys.
 *
 * Used by the dashboard Agent/API card to decide between the full "Generate key"
 * card and the collapsed "Agents connected" status row.
 *
 * Returns `count: 0` for users without API access (Free plan) — we render the
 * pre-key card in that case so they see the upsell, not an empty status row.
 */

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getApiKeys, ApiAccessForbiddenError } from '@/lib/api/api-keys'

type UseApiKeyCountOptions = {
  /** When true, count every key (including revoked). Default: active keys only. */
  fetchAll?: boolean
}

export function useApiKeyCount(
  propertyId: string | null | undefined,
  options?: UseApiKeyCountOptions,
): {
  count: number
  isLoading: boolean
} {
  const { token } = useAuth()
  const fetchAll = options?.fetchAll ?? false
  const enabled = !!propertyId && !!token

  const { data, isLoading } = useQuery({
    queryKey: ['api-key-count', propertyId, fetchAll ? 'all' : 'active'],
    queryFn: async (): Promise<number> => {
      try {
        const keys = await getApiKeys(propertyId!, token!)
        if (!Array.isArray(keys)) return 0
        return fetchAll ? keys.length : keys.filter((k) => k.isActive).length
      } catch (err) {
        if (err instanceof ApiAccessForbiddenError) return 0
        // Network / auth errors — fail silent for a status indicator
        return 0
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  return { count: data ?? 0, isLoading: enabled && isLoading }
}
